import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // { "id": 2, "email": "a1@b.co", "full_name": "Lucky r", "is_active": true, "subscription_plan": "pro", "subscription_status": "active", "subscription_start_date": "2026-01-20T18:10:07.321827", "subscription_end_date": "2026-02-19T18:10:07.321849", "created_at": "2026-01-19T07:56:02.904200", "updated_at": "2026-01-20T18:10:07.322858" }
    //user model
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem('token');
            const storedRefreshToken = localStorage.getItem('refreshToken');

            if (storedToken) {
                try {
                    axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
                    const response = await axios.get(`${API_BASE_URL}/api/user/profile`);
                    setUser(response.data);

                    // Ensure localStorage has user details
                    if (response.data.email) localStorage.setItem('userEmail', response.data.email);
                    if (response.data.full_name) localStorage.setItem('userName', response.data.full_name);

                    setToken(storedToken);
                    setRefreshToken(storedRefreshToken);
                } catch (error) {
                    console.error("Auth initialization failed:", error);
                    // Try refreshing before logging out if it was a 401
                    if (error.response?.status === 401 && storedRefreshToken) {
                        try {
                            const refreshResponse = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
                                refresh_token: storedRefreshToken
                            });
                            const { access_token, refresh_token: newRefreshToken, user_id, email, subscription_plan } = refreshResponse.data;
                            login(access_token, newRefreshToken, { id: user_id, email, subscription_plan });
                        } catch (refreshErr) {
                            logout();
                        }
                    } else {
                        logout();
                    }
                }
            }
            setLoading(false);
        };

        initAuth();
    }, [token]);

    // Axios interceptors
    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                const currentToken = localStorage.getItem('token');
                if (currentToken) {
                    config.headers['Authorization'] = `Bearer ${currentToken}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    const storedRefreshToken = localStorage.getItem('refreshToken');

                    if (storedRefreshToken) {
                        try {
                            const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
                                refresh_token: storedRefreshToken
                            });

                            const { access_token, refresh_token: newRefreshToken } = response.data;

                            localStorage.setItem('token', access_token);
                            localStorage.setItem('refreshToken', newRefreshToken);
                            setToken(access_token);
                            setRefreshToken(newRefreshToken);

                            axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
                            originalRequest.headers['Authorization'] = `Bearer ${access_token}`;

                            return axios(originalRequest);
                        } catch (refreshError) {
                            console.error("Token refresh failed:", refreshError);
                            logout();
                        }
                    } else {
                        logout();
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    const login = useCallback((newToken, newRefreshToken, userData) => {
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        setToken(newToken);
        setRefreshToken(newRefreshToken);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setToken(null);
        setRefreshToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
    }, []);

    /**
     * Refresh user profile data from the server
     * Useful after subscription upgrades or profile changes
     */
    const refreshUser = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/user/profile`);
            setUser(response.data);
            return response.data;
        } catch (error) {
            console.error("Failed to refresh user:", error);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{ user, token, login, logout, loading, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
